import React, { useContext, useMemo, useState } from "react";
import { useEffect } from "react";
import { useNavigate, useParams, Outlet } from "react-router-dom";
import { currentUser } from "system/InfegyUser";

export interface WidgetInteractionInfo {
  newWidgetID?: string;
  selectedWidgetIDs?: string[];
  setNewWidgetID?: (widgetID: string | undefined) => void;
  setSelectedWidgetIDs?: (widgetIDs: Array<string> | undefined) => void;
}

const WidgetInteractionContext = React.createContext<WidgetInteractionInfo | null>(null);
const WidgetInteractionProvider = WidgetInteractionContext.Provider;

// Use this function to access widget interaction info, such as selections and new widgets
export const useWidgetInteraction = () => {
  const widgetInteractionContext = useContext(WidgetInteractionContext);
  if (!widgetInteractionContext) {
    throw new Error(
      "useWidgetInteraction has to be used within <WidgetInteractionProvider>"
    );
  }
  return widgetInteractionContext;
};

export default function Dashboard(): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const defaultDashboardId = currentUser()?.preferences?.default_dashboard;

  const { dashboardId } = useParams();

  const navigate = useNavigate();

  const [ newWidgetID, setNewWidgetID ] = useState<string | undefined>(undefined);
  const [ selectedWidgetIDs, setSelectedWidgetIDs ] = useState<Array<string> | undefined>(undefined);

  const widgetInteractionInfo = useMemo(() => {
    return {
      newWidgetID,
      selectedWidgetIDs,
      setNewWidgetID,
      setSelectedWidgetIDs
    };
  }, [newWidgetID, selectedWidgetIDs]);

  // Check to make sure the user is navigating to a specific dashboard.
  // If not, we need to redirect them.
  useEffect(() => {
    // If the user has navigated to a specific dashboard, all is good and no actions need to be taken
    if (dashboardId) {
      return;
    } else {
      navigate("/start", { replace: true });
    }

    // If we already have a default dashboard id, redirect there.
    if (defaultDashboardId) {
      navigate(defaultDashboardId, { replace: true });
    } else {
      navigate("/start", { replace: true });
    }
  }, [dashboardId]);

  return (
  <WidgetInteractionProvider value={widgetInteractionInfo}>  
    <Outlet/>
  </WidgetInteractionProvider>
  );
}